<script setup lang="ts">
const { closePreview, selectedGame, isPreviewOpen, rememberAsLastPreview } =
  useGameMobilePreview();

const router = useRouter();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const casinoStore = useCasinoStore();

const currentGameId = computed(() => {
  const id = selectedGame.value?.id;

  return id ? Number(id) : 0;
});

const gameLink = computed(() => {
  if (!selectedGame.value?.slug) return '/';

  return `/games/${selectedGame.value.slug.toLowerCase()}`;
});

function playGame() {
  const target = gameLink.value;
  const gameToRemember = selectedGame.value;

  closePreview();
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }
  if (gameToRemember) rememberAsLastPreview(gameToRemember);
  casinoStore.isGameModalOpen = true;
  router.push(target);
}

// Fetches independentes: cada um tem seu próprio loading flag. Assim
// estatísticas e mais-pagou trocam de skeleton pro real cada um no seu
// tempo, sem esperar o outro.
const { fetchGameStatisticsBySlug } = useGamesStatistics();
const { getGamesFromCore } = useCasino();

const statistics = ref<GameStatisticsByPeriod | null>(null);
const mostPaidGames = ref<Game[] | null>(null);
const isStatisticsLoading = ref(false);
const isMostPaidLoading = ref(false);

async function loadStatistics(slug: string) {
  isStatisticsLoading.value = true;
  statistics.value = null;
  try {
    statistics.value = (await fetchGameStatisticsBySlug(slug)) || null;
  } catch {
    statistics.value = null;
  } finally {
    isStatisticsLoading.value = false;
  }
}

async function loadMostPaid() {
  // Resultado nao depende do jogo selecionado — busca uma vez e reaproveita
  // enquanto o componente estiver montado. Trocar de jogo nao redispara.
  if (mostPaidGames.value && mostPaidGames.value.length > 0) return;
  if (isMostPaidLoading.value) return;

  isMostPaidLoading.value = true;
  try {
    const res = await getGamesFromCore([
      ['sort_by', 'turnover'],
      ['sort_dir', 'desc'],
      ['per_page', '10'],
      ['page', 1],
    ]);

    mostPaidGames.value = (res?.data || []) as unknown as Game[];
  } catch {
    mostPaidGames.value = [];
  } finally {
    isMostPaidLoading.value = false;
  }
}

watch(
  () => selectedGame.value?.slug,
  (slug) => {
    if (!slug) return;
    loadStatistics(slug.toLowerCase());
    loadMostPaid();
  },
  { immediate: true },
);

// Lock body scroll while open
watch(isPreviewOpen, (open) => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = open ? 'hidden' : '';
});

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = '';
  }
});

// Trava o rubber-band do iOS: bloqueia touchmove quando o usuário tenta
// scrollar além do início/fim do .mgp-stack. Sem isso, o iOS Safari "estica"
// o conteúdo (efeito chiclete) mesmo com overscroll-behavior: none.
const stackRef = ref<HTMLElement | null>(null);
let touchStartY = 0;
let touchStartX = 0;

function onTouchStart(e: TouchEvent) {
  touchStartY = e.touches[0]?.clientY ?? 0;
  touchStartX = e.touches[0]?.clientX ?? 0;
}

function onTouchMove(e: TouchEvent) {
  const el = stackRef.value;

  if (!el) return;

  const currentY = e.touches[0]?.clientY ?? 0;
  const currentX = e.touches[0]?.clientX ?? 0;
  const deltaY = currentY - touchStartY;
  const deltaX = currentX - touchStartX;

  // Só interfere em gesto predominantemente vertical. Sem isso, swipers
  // horizontais aninhados (vistos recentes / mais pagou) ficavam travados
  // porque qualquer jitter vertical no topo do scroll caía no preventDefault.
  if (Math.abs(deltaX) > Math.abs(deltaY)) return;

  const atTop = el.scrollTop <= 0;
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

  // Puxando pra baixo no topo, ou pra cima no fim → cancela pra não esticar.
  if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
    e.preventDefault();
  }
}

watch(
  [isPreviewOpen, stackRef],
  ([open, el]) => {
    if (!el) return;
    if (open) {
      el.addEventListener('touchstart', onTouchStart, { passive: true });
      el.addEventListener('touchmove', onTouchMove, { passive: false });
    } else {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    }
  },
  { flush: 'post' },
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isPreviewOpen"
      class="mgp-overlay"
      @click.self="closePreview"
    >
      <div
        class="mgp-backdrop"
        @click="closePreview"
      />

      <Transition
        appear
        name="slide-up"
      >
        <div
          v-if="isPreviewOpen && selectedGame"
          ref="stackRef"
          class="mgp-stack"
        >
          <!-- Thumb fora do sheet pra poder sair acima do modal -->
          <div class="mgp-thumb-wrapper">
            <div class="mgp-thumb">
              <CasinoMediaApostatudo
                :game="selectedGame"
                no-link
              />
            </div>
          </div>

          <div class="mgp-sheet">
            <!-- Topbar: menu + close -->
            <header class="mgp-topbar">
              <CasinoGameActionsMenu
                :game="selectedGame"
                share-only
              />

              <button
                aria-label="Fechar"
                class="mgp-icon-btn"
                type="button"
                @click="closePreview"
              >
                <svg
                  fill="none"
                  height="10"
                  viewBox="0 0 14 14"
                  width="10"
                >
                  <path
                    d="M1 1L13 13M13 1L1 13"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-width="2"
                  />
                </svg>
              </button>
            </header>

            <!-- Título + provedora -->
            <div class="mgp-title-block">
              <h2 class="mgp-name">{{ selectedGame.name }}</h2>
              <p class="mgp-provider">
                {{ selectedGame.provider?.name || '' }}
              </p>
            </div>

            <!-- JOGAR -->
            <button
              class="mgp-play-btn"
              type="button"
              @click="playGame"
            >
              <svg
                class="mgp-play-icon"
                fill="none"
                height="13"
                viewBox="0 0 11 13"
                width="11"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 6.5L0 0V13L11 6.5Z"
                  fill="currentColor"
                />
              </svg>
              JOGAR
            </button>

            <!-- Chips de tags -->
            <CasinoGameTagChips
              class="mgp-chips-wrapper"
              :reset-key="currentGameId"
              :tags="selectedGame.tags"
            />

            <!-- Estatísticas — só logado; recebe dados via prop (parent coordena fetch) -->
            <CasinoGameStatisticsCarousel
              v-if="isAuthenticated"
              class="mgp-statistics"
              :is-loading="isStatisticsLoading"
              :statistics="statistics"
            />

            <!-- Vistos recentes (só logado) — acima do mais pagou -->
            <CasinoGameLastPlayedPreview
              :exclude-slug="selectedGame?.slug"
              :per-row="[5.3, 4.25, 3.65]"
              :space-between="[12, 10, 8]"
              @see-more="closePreview"
            />

            <!-- Mais pagou — recebe dados via prop (parent coordena fetch) -->
            <PerfilOficialMaisPagouApostatudo
              class="mgp-most-paid"
              :class="{ 'mgp-most-paid--spaced': !isAuthenticated }"
              :exclude-slug="selectedGame?.slug"
              :external-loading="isMostPaidLoading"
              :games="mostPaidGames"
              :per-row="[5.3, 4.25, 3.65]"
              :space-between="[12, 10, 8]"
              @see-more="closePreview"
            />

            <!-- Compliance oficial (ENG-404): +18 + advertência + operador -->
            <BaseComplianceInfo class="mgp-compliance" />

            <div class="mgp-safe-bottom" />
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style>
/* Transições "fade-overlay" e "slide-up" — também declaradas em
   assets/css/main.scss (globais). Replicadas aqui sem scope pra garantir
   que o Vue Transition aplique mesmo se o SCSS global não recarregar. */
.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity 0.25s ease;
}
.fade-overlay-enter-from,
.fade-overlay-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  animation: slide-up-spring 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.slide-up-leave-active {
  animation: slide-up-spring 0.22s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes slide-up-spring {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped>
.mgp-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.mgp-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.mgp-stack {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  /* Margem de segurança no topo: garante que o thumb nunca cole no
     topo da tela (respeita notch/status-bar). Combinado com o cap em
     100dvh, força o conteúdo a iniciar abaixo dessa folga. */
  padding-top: calc(env(safe-area-inset-top, 0px) + 24px);
  /* Cap em 100dvh; o scroll fica AQUI (não no .mgp-sheet) pra que o
     thumb também role pra fora do viewport quando o usuário scrolla.
     Antes, com overflow no sheet, o thumb (irmão fora do scroll) ficava
     sticky por cima. */
  max-height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  /* Sem -webkit-overflow-scrolling: touch — ele força momentum legacy que
     habilita o rubber-band. iOS 13+ tem momentum nativo sem ele. */
  overscroll-behavior: none;
}

.mgp-sheet {
  position: relative;
  flex: 1 0 auto;
  padding: 50px 16px 0;
  background: linear-gradient(180deg, #0a0f2e 0%, #020716 100%);
  border-radius: 18px 18px 0 0;
}

/* Topbar */
.mgp-topbar {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 5;
}

.mgp-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  border: none;
  cursor: pointer;
}

.mgp-icon-btn:hover {
  background: rgba(255, 255, 255, 0.16);
}

/* Thumb elevada — irmã do sheet, fica acima e o sheet sobrepõe a parte inferior */
.mgp-thumb-wrapper {
  position: relative;
  z-index: 11;
  display: flex;
  justify-content: center;
  margin-bottom: -50px;
  pointer-events: none;
}

.mgp-thumb {
  width: 110px;
  aspect-ratio: 6 / 7;
  border-radius: 12px;
  position: relative;
  pointer-events: auto;
}

/* Título */
.mgp-title-block {
  text-align: center;
  padding: 10px 4px;
  user-select: none;
  -webkit-user-select: none;
}

.mgp-name {
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 4px;
}

.mgp-provider {
  color: #98a2b3;
  font-size: 13px;
  font-weight: 500;
  margin: 0;
}

/* JOGAR */
.mgp-play-btn {
  position: relative;
  overflow: hidden;
  display: flex;
  width: 100%;
  height: 35px;
  padding: 0 10px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  border-radius: 6px;
  border: 1px solid #fc9984;
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  color: #ffffff;
  text-align: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.1s ease;
}

/* Shimmer shine — mesmo efeito do .shinyCard nos queridinhos */
.mgp-play-btn::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -100%;
  width: 50%;
  height: 200%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  transform: skewX(-25deg);
  animation: shimmerSlide 1.5s ease-out infinite;
  pointer-events: none;
  z-index: 1;
}

.mgp-play-btn > * {
  position: relative;
  z-index: 2;
}

@keyframes shimmerSlide {
  0% {
    left: -100%;
  }
  100% {
    left: 150%;
  }
}

.mgp-play-btn:hover {
  filter: brightness(1.08);
}

.mgp-play-btn:active {
  transform: scale(0.98);
}

.mgp-play-icon {
  width: 11px;
  height: 13px;
  flex-shrink: 0;
  display: block;
}

/* Chips wrapper — bleed lateral do sheet pra encostar nas bordas, igual antes */
.mgp-chips-wrapper {
  padding: 16px 16px 0;
  margin: 0 -16px;
}

/* Estatísticas */
.mgp-statistics {
  margin-bottom: 8px;
}

/* Mais pagou */
.mgp-most-paid {
  padding-top: 0;
  margin-bottom: 0 !important;
}

/* Deslogado: estatísticas e "vistos recentes" ficam ocultos, então adiciona
   respiro entre os chips/botão e o "mais pagou" pra não colarem. */
.mgp-most-paid--spaced {
  margin-top: 20px;
}

/* Reduz o valor "Pagou hoje" dos cards dentro do preview (vistos recentes
   e mais pagou). Targeta a classe gerada do CSS module do MediaApostatudo. */
.mgp-stack :deep([class*='payTodayValue']) {
  font-size: 8px !important;
}

.mgp-most-paid-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.mgp-most-paid-icon {
  font-size: 22px;
  color: #ffffff;
}

.mgp-most-paid-title {
  flex: 1;
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.mgp-see-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #00c9ff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
}

/* Compliance — respiro acima pra separar do "mais pagou" */
.mgp-compliance {
  margin-top: 20px;
}

.mgp-safe-bottom {
  height: env(safe-area-inset-bottom, 16px);
  min-height: 16px;
}

/* Transições "fade-overlay" e "slide-up" são globais — assets/css/main.scss */
</style>
